/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import { MinusIcon, PlusIcon } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { forwardRef, useId } from 'react'
import type { RegisterOptions } from 'react-hook-form'
import { Controller, useFormContext, useFormState } from 'react-hook-form'

import { cn, mergeRefs } from '../../../lib/utils'
import { Field, FieldDescription, FieldError, FieldLabel } from '../../ui/field'
import { InputGroup, InputGroupAddon } from '../../ui/input-group'
import { NumberField } from '../../ui/number-field'

type NumberFieldRootSharedProps = Pick<
  ComponentProps<typeof NumberField.Root>,
  | 'min'
  | 'max'
  | 'step'
  | 'smallStep'
  | 'largeStep'
  | 'allowOutOfRange'
  | 'allowWheelScrub'
  | 'snapOnStep'
  | 'format'
  | 'locale'
  | 'readOnly'
  | 'disabled'
>

function formValueToNumberFieldValue(value: unknown): number | null {
  if (value === '' || value === undefined || value === null) {
    return null
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value === 'string') {
    const n = Number(value)
    return Number.isFinite(n) ? n : null
  }
  return null
}

export type NumberInputFormFieldProps = {
  disabled?: boolean
  fieldProps?: ComponentProps<typeof Field>
  helperText?: string
  inputGroupProps?: ComponentProps<typeof InputGroup>
  inputProps?: ComponentProps<typeof NumberField.Input> & Partial<NumberFieldRootSharedProps>
  label?: string
  leftElement?: ReactNode
  name: string
  placeholder?: string
  required?: boolean
  rightElement?: ReactNode
  validation?: RegisterOptions
}

export const NumberInputFormField = forwardRef<HTMLInputElement, NumberInputFormFieldProps>((props, forwardedRef) => {
  const { control } = useFormContext()
  const { errors } = useFormState({ control, name: props.name })

  const error = errors[props.name]
  const invalid = !!error

  const generatedId = useId()
  const rawInputProps = props.inputProps ?? {}
  const {
    min,
    max,
    step,
    smallStep,
    largeStep,
    allowOutOfRange,
    allowWheelScrub,
    snapOnStep,
    format,
    locale,
    readOnly,
    disabled: inputDisabled,
    className: inputClassName,
    id: inputPropsId,
    ref: inputRef,
    placeholder: inputPlaceholder,
    ...restInputProps
  } = rawInputProps

  const controlId = inputPropsId ?? generatedId
  const placeholder = props.placeholder ?? inputPlaceholder

  return (
    <Field {...props.fieldProps} data-invalid={invalid}>
      {props.label !== undefined && (
        <FieldLabel className="flex items-center gap-1" htmlFor={controlId}>
          {props.label}
          {props.required ? (
            <span aria-hidden className="text-destructive">
              *
            </span>
          ) : null}
        </FieldLabel>
      )}
      <Controller
        control={control}
        name={props.name}
        render={({ field }) => {
          return (
            <InputGroup
              {...props.inputGroupProps}
              className={cn('max-w-[50%] flex-1', props.inputGroupProps?.className)}
            >
              {props.leftElement ? <InputGroupAddon align="inline-start">{props.leftElement}</InputGroupAddon> : null}
              <NumberField.Root
                allowOutOfRange={allowOutOfRange}
                allowWheelScrub={allowWheelScrub}
                data-invalid={invalid || undefined}
                disabled={field.disabled || props.disabled || inputDisabled}
                format={format}
                id={controlId}
                largeStep={largeStep}
                locale={locale}
                max={max}
                min={min}
                name={field.name}
                onValueChange={(value) => {
                  field.onChange(value === null ? '' : value)
                }}
                readOnly={readOnly}
                required={props.required}
                smallStep={smallStep}
                snapOnStep={snapOnStep}
                step={step}
                value={formValueToNumberFieldValue(field.value)}
                variant="ghost"
              >
                <NumberField.Group className="min-w-0 flex-1">
                  <NumberField.Decrement>
                    <MinusIcon aria-hidden />
                  </NumberField.Decrement>
                  <NumberField.Input
                    aria-invalid={invalid}
                    className={cn(inputClassName)}
                    data-slot="input-group-control"
                    placeholder={placeholder}
                    ref={mergeRefs(forwardedRef, inputRef)}
                    {...restInputProps}
                    onBlur={(event) => {
                      restInputProps.onBlur?.(event)
                      field.onBlur()
                    }}
                  />
                  <NumberField.Increment>
                    <PlusIcon aria-hidden />
                  </NumberField.Increment>
                </NumberField.Group>
              </NumberField.Root>
              {props.rightElement ? <InputGroupAddon align="inline-end">{props.rightElement}</InputGroupAddon> : null}
            </InputGroup>
          )
        }}
        rules={props.validation}
      />
      {props.helperText !== undefined ? <FieldDescription>{props.helperText}</FieldDescription> : null}
      {error?.message ? <FieldError>{String(error.message)}</FieldError> : null}
    </Field>
  )
})

NumberInputFormField.displayName = 'NumberInputFormField'
